import React, { useEffect, useState } from 'react';
import { Shield, ShieldOff, AlertTriangle, Settings as SettingsIcon, Save, Loader2 } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { fmtDateTime } from '../../lib/docs';

const FORM_LABELS = {
  contact: 'Contact / Callback form',
  signup: 'Member registration',
  login_after_failures: 'Login after suspicious attempts',
  booking: 'Public appointment booking',
  password_reset: 'Password reset',
  document_upload: 'Document upload (auth required)',
};

export default function AdminTurnstilePage() {
  const [settings, setSettings] = useState(null);
  const [failures, setFailures] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [s, f] = await Promise.all([
        api.turnstileSettings(),
        api.turnstileFailures({ limit: 50 }),
      ]);
      setSettings(s);
      setFailures(f);
    } catch {
      toast.error('Could not load Turnstile settings');
    }
  };
  useEffect(() => { load(); }, []);

  const toggleForm = (key) => {
    setSettings((s) => ({ ...s, forms: { ...s.forms, [key]: !s.forms[key] } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const out = await api.turnstileSettingsUpdate({
        enabled: settings.enabled,
        mode: settings.mode || 'managed',
        forms: settings.forms,
        login_failure_threshold: settings.login_failure_threshold || 3,
      });
      setSettings(out);
      toast.success('Turnstile settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };

  if (!settings) {
    return (
      <AdminLayout title="Turnstile" subtitle="Cloudflare bot protection">
        <div className="text-neutral-400 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />Loading…
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Turnstile" subtitle="Cloudflare Turnstile bot protection">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Settings */}
        <section className="lg:col-span-7 space-y-5">
          <div className="rounded-xl bg-white border border-neutral-100 p-6">
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <h2 className="text-[17px] font-bold text-[#14110f] flex items-center gap-2">
                  {settings.enabled ? <Shield className="w-4 h-4 text-emerald-600" />
                                     : <ShieldOff className="w-4 h-4 text-rose-600" />}
                  Global protection
                </h2>
                <p className="text-[12.5px] text-neutral-500 mt-0.5">
                  Master switch — turns Turnstile on or off site-wide.
                </p>
              </div>
              <button data-testid="ts-enabled-toggle"
                      role="switch" aria-checked={settings.enabled}
                      onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
                      className={`relative inline-flex h-6 w-11 rounded-full transition ${settings.enabled ? 'bg-[#f5b300]' : 'bg-neutral-300'}`}>
                <span className={`inline-block w-5 h-5 bg-white rounded-full shadow transform transition ${settings.enabled ? 'translate-x-5' : 'translate-x-0.5'} self-center`} />
              </button>
            </div>

            <div className="border-t border-neutral-100 pt-5">
              <p className="text-[11.5px] uppercase tracking-wider text-neutral-500 font-semibold mb-3">
                Per-form enforcement
              </p>
              <ul className="space-y-2">
                {Object.entries(FORM_LABELS).map(([key, label]) => (
                  <li key={key} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-md border border-neutral-100">
                    <span className="text-[13.5px] text-[#14110f]">{label}</span>
                    <button data-testid={`ts-form-${key}`}
                            role="switch" aria-checked={settings.forms?.[key] || false}
                            disabled={!settings.enabled}
                            onClick={() => toggleForm(key)}
                            className={`relative inline-flex h-5 w-9 rounded-full transition ${settings.forms?.[key] ? 'bg-[#f5b300]' : 'bg-neutral-300'} ${settings.enabled ? '' : 'opacity-40'}`}>
                      <span className={`inline-block w-4 h-4 bg-white rounded-full shadow transform transition ${settings.forms?.[key] ? 'translate-x-4' : 'translate-x-0.5'} self-center`} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-t border-neutral-100 pt-5 mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-[11.5px] uppercase tracking-wider text-neutral-500 font-semibold">Verification mode</span>
                <select data-testid="ts-mode"
                        value={settings.mode || 'managed'}
                        onChange={(e) => setSettings({ ...settings, mode: e.target.value })}
                        className="mt-1 w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] text-[#14110f] bg-white focus:border-[#f5b300] outline-none">
                  <option value="managed">Managed (recommended)</option>
                  <option value="non_interactive">Non-interactive</option>
                  <option value="invisible">Invisible</option>
                </select>
                <p className="text-[11px] text-neutral-400 mt-1">
                  Mode is set in Cloudflare Dashboard — change there for live effect.
                </p>
              </label>
              <label className="block">
                <span className="text-[11.5px] uppercase tracking-wider text-neutral-500 font-semibold">Login fail threshold</span>
                <input data-testid="ts-threshold"
                       type="number" min={1} max={20}
                       value={settings.login_failure_threshold || 3}
                       onChange={(e) => setSettings({ ...settings, login_failure_threshold: parseInt(e.target.value, 10) || 3 })}
                       className="mt-1 w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] text-[#14110f] bg-white focus:border-[#f5b300] outline-none" />
                <p className="text-[11px] text-neutral-400 mt-1">
                  Failed logins from one IP in 15 min before captcha is required.
                </p>
              </label>
            </div>

            <div className="mt-5 flex items-center justify-between">
              <p className="text-[12px] text-neutral-500">
                Site key: <code className="px-1.5 py-0.5 rounded bg-neutral-100 text-[11.5px] text-[#14110f]">{process.env.REACT_APP_TURNSTILE_SITEKEY || 'not configured'}</code>
              </p>
              <button data-testid="ts-save"
                      onClick={save} disabled={saving}
                      className="inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] disabled:opacity-60 text-[#14110f] font-semibold px-4 py-2 rounded-md text-[13px]">
                <Save className="w-3.5 h-3.5" />{saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </section>

        {/* Failure log */}
        <section className="lg:col-span-5">
          <div className="rounded-xl bg-white border border-neutral-100 p-6">
            <h2 className="text-[15px] font-bold text-[#14110f] flex items-center gap-2 mb-4">
              <AlertTriangle className="w-4 h-4 text-rose-600" />Recent failures ({failures.length})
            </h2>
            {failures.length === 0 ? (
              <p className="text-[12.5px] text-neutral-500 italic py-4">No failures recorded yet. Either no bots tried, or verification is doing its job.</p>
            ) : (
              <ul data-testid="ts-failures" className="space-y-1.5 max-h-[520px] overflow-y-auto">
                {failures.map((f) => (
                  <li key={f.id} className="rounded-md border border-neutral-100 px-3 py-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[12.5px] font-bold text-[#14110f]">{f.form}</span>
                      <span className="text-[11px] text-neutral-400">{fmtDateTime(f.created_at)}</span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-[11.5px]">
                      <span className="px-2 py-0.5 rounded bg-rose-50 text-rose-700 font-semibold">{f.reason}</span>
                      <span className="text-neutral-500 truncate">{f.ip || '—'}</span>
                    </div>
                    {(f.error_codes || []).length > 0 && (
                      <p className="mt-0.5 text-[11px] text-neutral-400">{(f.error_codes || []).join(', ')}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}
